import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import noop from 'lodash/noop';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Helmet } from "react-helmet-async";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack'
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { RouterLink } from '@src/components/core/link';
import { FormBase, TextField, Submit } from '@src/components/form';
import { useAuth, useMessage } from '@src/context/common';
import { useForm } from '@src/hooks';
import paths from '@src/paths';
import { DynamicLogo } from '@src/components/core/logo';
import config from '@src/config';


const metadata = { title: `Sign in | ${config.SITE.title}` };

const schema = Yup.object().shape({
  password: Yup.string()
    .required('This field is required'),
    // .min(8, 'Too Short!'),
  email: Yup.string('Enter your Email')
    .email('Enter a valid email')
    .required('Email is required'),
});

const DEFAULT_VALUES = {
  email: '',
  password: '',
};

function LoginForm({ onSubmit, onError, onSuccess }) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ schema, defaultValues: DEFAULT_VALUES });
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (e) => {
    e.preventDefault();
  };

  return (
    <FormBase onSubmit={handleSubmit({ onSubmit, onError, onSuccess })}>
      <TextField
        label="Email address"
        control={control}
        margin="none"
        required
        fullWidth
        id="email"
        name="email"
        autoFocus
      />

      <TextField
        label="Password"
        control={control}
        margin="none"
        required
        fullWidth
        type={showPassword ? 'text' : 'password'}
        id="password"
        name="password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <Submit
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        fullWidth={true}
        loading={isSubmitting}
      >
        Sign in
      </Submit>
    </FormBase>
  );
}

LoginForm.displayName='LoginForm';
LoginForm.propTypes = {
  onSubmit: PropTypes.func,
  onError: PropTypes.func,
  OnSuccess: PropTypes.func,
};
LoginForm.defaultProps = {
  onSubmit: noop,
  onError: noop,
  OnSuccess: noop,
};

function normalizeSlug(value = '') {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function getMembershipDashboardSlug(membership = {}) {
  const clientSlug = membership?.client_slug || membership?.client_id;
  if (clientSlug) {
    return clientSlug;
  }
  const parentName = membership?.parent_company_name;
  if (parentName) {
    return normalizeSlug(parentName);
  }
  return membership?.parent_company_id || null;
}

function getHomePath(user) {
  const memberships = user?.client_memberships || [];
  const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;
  if (isClientMember) {
    const primary = memberships[0] || {};
    const slug = getMembershipDashboardSlug(primary);
    const status = primary?.client_status;
    const isOnboarding = status === 'new_business' || status === 'onboarding';
    if (slug) {
      return isOnboarding ? paths.onboarding.forClient(slug) : paths.clientDashboard(slug);
    }
    return paths.clients;
  }
  return paths.dashboard.dailyPulse;
}

function Login() {
  const { login, isAuthenticated, state } = useAuth();
  const message = useMessage();

  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(getHomePath(state?.user), { replace: true });
    }
  }, [isAuthenticated, navigate, state?.user]);

  const handleOnSubmit = useCallback(
    (values) => {
      return login(values).then((resp) => {
        const userFromResponse = resp?.data?.user;
        const target = getHomePath(userFromResponse || state?.user);
        navigate(target, { replace: true });
        return resp;
      });
    },
    [login, navigate, state?.user]
  );

  const handleOnSuccess = useCallback(() => {
    message.close();
  }, [message]);

  const handleOnError = useCallback(
    (_, { isFieldError }) => {
      if (isFieldError) {
        message.error('Unable to log in with provided credentials.');
      } else {
        message.error('Unexpected server error.');
      }
    },
    [message],
  );

  return (
    <React.Fragment>
      <Helmet>
        <title>{metadata.title}</title>
      </Helmet>
      <Stack spacing={4}>
        <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Box component={RouterLink} href={paths.home} sx={{ display: 'inline-block', fontSize: 0 }}>
            <DynamicLogo colorDark="light" colorLight="dark" height="44px" width="auto" />
          </Box>
          <Stack spacing={0.5}>
            <Typography variant="h5">Sign in to your account</Typography>
            <Typography color="text.secondary" variant="body2">
              Campaign performance, lead analytics, and growth tools—all in one place.
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={2}>
          <LoginForm
            onSubmit={handleOnSubmit}
            onSuccess={handleOnSuccess}
            onError={handleOnError}
          />
          <Box sx={{ textAlign: 'center' }}>
            <Link component={RouterLink} href={paths.auth.cognito.resetPassword} variant="subtitle2">
              Forgot password?
            </Link>
          </Box>
        </Stack>
      </Stack>
    </React.Fragment>
  );
}


Login.displayName = 'Login';
export default Login;
